import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

const ResetPassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { updatePassword } = useAuth();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [errors, setErrors] = useState({});
  const [sessionReady, setSessionReady] = useState(false);
  const [sessionError, setSessionError] = useState(false);

  // Supabase envía al usuario aquí con un token en el hash de la URL.
  // onAuthStateChange lo procesa automáticamente y dispara PASSWORD_RECOVERY.
  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'PASSWORD_RECOVERY') {
        setSessionReady(true);
      }
    });

    // Si ya hay sesión activa de recovery (recarga de página), verificar
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) setSessionReady(true);
    });

    // Timeout: si en 10s no llega el evento, el link es inválido/expirado
    const timeout = setTimeout(() => {
      setSessionReady(prev => {
        if (!prev) setSessionError(true);
        return prev;
      });
    }, 10000);

    return () => {
      subscription?.unsubscribe();
      clearTimeout(timeout);
    };
  }, []);

  const validate = () => {
    const newErrors = {};
    if (!password) {
      newErrors.password = 'La contraseña es requerida.';
    } else if (password.length < 8) {
      newErrors.password = 'Mínimo 8 caracteres.';
    }
    if (password !== confirmPassword) {
      newErrors.confirmPassword = 'Las contraseñas no coinciden.';
    }
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const validationErrors = validate();
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      return;
    }
    setLoading(true);
    setErrors({});
    const { error } = await updatePassword(password);
    setLoading(false);
    if (error) {
      const msg = error?.message || 'No se pudo actualizar la contraseña.';
      const friendlyMsg = msg.includes('same_password') || msg.toLowerCase().includes('same password')
        ? 'La nueva contraseña debe ser diferente a la contraseña actual.'
        : msg;
      setErrors({ submit: friendlyMsg });
    } else {
      setDone(true);
    }
  };

  // Link inválido o expirado
  if (sessionError) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-card border border-border rounded-xl shadow-elevation-lg p-8 text-center space-y-4">
          <div className="flex justify-center">
            <div className="w-14 h-14 rounded-full bg-destructive/10 flex items-center justify-center">
              <Icon name="LinkOff" size={28} className="text-destructive" />
            </div>
          </div>
          <h2 className="text-xl font-semibold text-foreground">Enlace inválido o expirado</h2>
          <p className="text-sm text-muted-foreground">
            Este enlace de restablecimiento ya no es válido. Solicita uno nuevo.
          </p>
          <Button variant="default" className="w-full" onClick={() => navigate('/forgot-password')}>
            Solicitar nuevo enlace
          </Button>
        </div>
      </div>
    );
  }

  // Esperando que Supabase procese el token del hash
  if (!sessionReady) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <div className="w-full max-w-md bg-card border border-border rounded-xl shadow-elevation-lg p-8 text-center space-y-4">
          <div className="flex justify-center">
            <div className="w-10 h-10 border-2 border-accent border-t-transparent rounded-full animate-spin" />
          </div>
          <p className="text-sm text-muted-foreground">Verificando enlace...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4 md:p-6 lg:p-8">
      <div className="w-full max-w-md">
        <div className="bg-card border border-border rounded-lg md:rounded-xl shadow-elevation-lg p-6 md:p-8 lg:p-10">

          {/* Header */}
          <div className="text-center mb-6 md:mb-8">
            <div className="flex justify-center mb-4">
              <div className="w-14 h-14 md:w-16 md:h-16 rounded-xl bg-accent/10 flex items-center justify-center">
                <Icon name="ShieldCheck" size={32} className="text-accent" />
              </div>
            </div>
            <h1 className="text-2xl md:text-3xl font-semibold text-foreground mb-2">
              Nueva contraseña
            </h1>
            <p className="text-sm text-muted-foreground">
              Elige una contraseña segura para tu cuenta.
            </p>
          </div>

          {done ? (
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="w-14 h-14 rounded-full bg-green-500/10 flex items-center justify-center">
                  <Icon name="CheckCircle" size={28} className="text-green-500" />
                </div>
              </div>
              <p className="text-sm font-medium text-foreground">
                ¡Contraseña actualizada con éxito!
              </p>
              <Button
                variant="default"
                className="w-full"
                onClick={() => navigate('/login')}
              >
                Ir al inicio de sesión
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-5">
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Nueva contraseña</label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    placeholder="Mínimo 8 caracteres"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    autoFocus
                    className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 pr-10 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Icon name={showPassword ? 'EyeOff' : 'Eye'} size={18} />
                  </button>
                </div>
                {errors.password && <p className="text-sm text-destructive">{errors.password}</p>}
              </div>

              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground">Confirmar contraseña</label>
                <input
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Repite la contraseña"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
                {errors.confirmPassword && <p className="text-sm text-destructive">{errors.confirmPassword}</p>}
              </div>

              {errors.submit && (
                <p className="text-sm text-destructive">{errors.submit}</p>
              )}

              <Button
                type="submit"
                variant="default"
                className="w-full"
                loading={loading}
              >
                Guardar nueva contraseña
              </Button>
            </form>
          )}
        </div>

        <div className="mt-6 text-center">
          <p className="text-xs text-muted-foreground">
            &copy; {new Date().getFullYear()} CatalogFlip. {t('embedSettings.footer.rights')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
